import { useColorMode } from '@chakra-ui/react'
import { AppName, AppVersionData, getAppDisplayName, maintenanceInfo, version } from '@hb/shared'
import 'firebase/firestore'
import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { useDocument } from '../hooks/backend/useDocument'
import { AppData } from '../types/app'

const getInitUnderMaintenance = (now: Date) => {
  if (!maintenanceInfo) return false
  const { startTime } = maintenanceInfo
  return startTime.localeCompare(now.toISOString()) <= 0
}
const useIsUnderMaintenance = () => {
  const [now] = useState(new Date())
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(getInitUnderMaintenance(now))

  useEffect(() => {
    if (!maintenanceInfo) return
    const { startTime } = maintenanceInfo
    const start = new Date(startTime)
    const timeUntilStart = start.getTime() - now.getTime()
    const timeout = setTimeout(() => {
      setIsUnderMaintenance(true)
    }, timeUntilStart)
    return () => clearTimeout(timeout)
  }, [now])

  return isUnderMaintenance
}
const useAppData = (appName: AppName): AppData => {
  const appDataCollection = useMemo(() => `apps/${appName}/${version}`, [appName])
  const appVersion = useDocument<AppVersionData>(appDataCollection, 'current')
  const appDisplayName = useMemo(() => getAppDisplayName(appName), [appName])
  const isUnderMaintenance = useIsUnderMaintenance()
  return {
    appName,
    appVersion,
    appDisplayName,
    isUnderMaintenance,
  }
}

export const AppContext = React.createContext<AppData>({
  appName: 'NONE',
  appDisplayName: 'NONE',
  isUnderMaintenance: false,
  appVersion: {
    data: null,
    loading: false,
    onDelete: async () => Promise.resolve(false),
    onSave: () => Promise.resolve(false),
    refetch: () => {},
    ref: null,
    error: null,
  },
})

export const useApp = () => useContext(AppContext)

export const AppProvider = ({ children, appName }: { children: ReactNode; appName: AppName }) => {
  const appData = useAppData(appName)

  const { setColorMode } = useColorMode()
  useEffect(() => {
    setColorMode('light')
  }, [setColorMode])
  return <AppContext.Provider value={appData}>{children}</AppContext.Provider>
}
